import React, { useRef, useEffect, useContext } from "react";
import { auth } from "../../firebase";
import { signOut } from "firebase/auth";
import lgLogo from "../../images/svg-logo/lgLogo-dark.svg";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";

function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const location = useLocation();
  const { pathname } = location;

  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);

  const trigger = useRef(null);
  const sidebar = useRef(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = (event) => {
      const { target } = event;
      if (!sidebar.current || !trigger.current) return;

      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      ) {
        return;
      }

      setSidebarOpen(false);
    };

    document.addEventListener("click", clickHandler);

    return () => {
      document.removeEventListener("click", clickHandler);
    };
  }, [setSidebarOpen, sidebarOpen, sidebar, trigger]);

  // close when esc key is pressed

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };

    document.addEventListener("keydown", keyHandler);

    return () => {
      document.removeEventListener("keydown", keyHandler);
    };
  }, [setSidebarOpen, sidebarOpen]);

  //sign out

  return (
    <aside
      ref={sidebar}
      className={`absolute left-0 top-0 z-20 flex h-screen w-72.5 flex-col overflow-y-hidden bg-purple-dark duration-300 ease-linear lg:static lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      {/* <!-- SIDEBAR HEADER --> */}
      <div className="flex items-center justify-between gap-14 px-6 py-5.5 lg:py-6.5">
        <NavLink to="/">
          <img src={lgLogo} alt="Logo" />
        </NavLink>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="block lg:hidden"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="#FFFFFF"
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m12.75 15 3-3m0 0-3-3m3 3h-7.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
        </button>
      </div>
      {/* <!-- SIDEBAR HEADER --> */}

      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
        {/* <!-- Sidebar Menu --> */}
        <nav className="mt-5 py-4 px-4 lg:mt-9 lg:px-6">
          {/* <!-- Menu Group --> */}
          <div>
            <h3 className="mb-4 ml-4 text-base font-display opacity-80 font-semibold text-white">
              MENU
            </h3>

            <ul className="mb-6 flex flex-col gap-1.5">
              {/* <!-- Menu Item Dashboard --> */}
              <li>
                <NavLink
                  to="/dashboard"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium font-display duration-300  text-white ease-in-out hover:bg-purple-7 ${
                    pathname.includes("dashboard") && "bg-purple-7"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
                    />
                  </svg>
                  Dashboard
                </NavLink>
              </li>
              {/* <!-- Menu Item Dashboard --> */}

              {/* <!-- Menu Item Budgeting --> */}
              <li>
                <NavLink
                  to="/budgeting"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium font-display duration-300  text-white ease-in-out hover:bg-purple-7 ${
                    pathname.includes("budgeting") && "bg-purple-7"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3"
                    />
                  </svg>
                  Budgeting
                </NavLink>
              </li>
              {/* <!-- Menu Item Budgeting --> */}

              {/* <!-- Menu Item Transaction --> */}
              <li>
                <NavLink
                  to="/transactions"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium font-display duration-300  text-white ease-in-out hover:bg-purple-7 ${
                    pathname.includes("transactions") && "bg-purple-7"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                    />
                  </svg>
                  Transactions
                </NavLink>
              </li>
              {/* <!-- Menu Item Transaction --> */}

              {/* <!-- Menu Item Goal --> */}
              <li>
                <NavLink
                  to="/goal"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium font-display duration-300  text-white ease-in-out hover:bg-purple-7 ${
                    pathname.includes("goal") && "bg-purple-7"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z"
                    />
                  </svg>
                  Saving Goals
                </NavLink>
              </li>
              {/* <!-- Menu Item Goal --> */}

              {/* <!-- Menu Item Report --> */}
              <li>
                <NavLink
                  to="/report"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium font-display duration-300  text-white ease-in-out hover:bg-purple-7 ${
                    pathname.includes("report") && "bg-purple-7"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
                    />
                  </svg>
                  Report
                </NavLink>
              </li>
              {/* <!-- Menu Item Report --> */}

              {/* <!-- Menu Item Forums --> */}
              <li>
                <NavLink
                  to="/forums"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium font-display duration-300  text-white ease-in-out hover:bg-purple-7 ${
                    pathname.includes("forums") && "bg-purple-7"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
                    />
                  </svg>
                  Forums
                </NavLink>
              </li>
              {/* <!-- Menu Item Forums --> */}

              {/* <!-- Menu Item Settings --> */}
              <li>
                <NavLink
                  to="/accounts"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium font-display duration-300  text-white ease-in-out hover:bg-purple-7 ${
                    pathname.includes("accounts") && "bg-purple-7"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                    />
                  </svg>
                  Accounts
                </NavLink>
              </li>
              {/* <!-- Menu Item Settings --> */}

              {/* <!-- Menu Item  Feedback --> */}
              <li>
                <NavLink
                  to="/feedback"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium font-display duration-300  text-white ease-in-out hover:bg-purple-7 ${
                    pathname.includes("feedback") && "bg-purple-7"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
                    />
                  </svg>
                  Feedback
                </NavLink>
              </li>
              {/* <!-- Menu Item Help/ Feedback --> */}

              {/* <!-- Menu Item Logout --> */}

              <li>
                <h3 className="mt-4 ml-4 text-base font-display opacity-80 font-semibold text-white">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      signOut(auth)
                        .then(() => {
                          // Sign-out successful.
                          dispatch({ type: "LOGOUT", payload: null });
                          navigate("/");
                        })
                        .catch((error) => {
                          alert("Error signing out:", error);
                        });
                    }}
                  >
                    LOG OUT
                  </button>
                </h3>
              </li>
              {/* <!-- Menu Item Logout --> */}
            </ul>
          </div>
        </nav>
        {/* <!-- Sidebar Menu --> */}
      </div>
    </aside>
  );
}

export default Sidebar;
